import { useEffect, useRef } from "react";
import { cn } from "@util/tailwind";

interface EnhancedAudioVisualizerProps {
    isActive: boolean;
    className?: string;
    color?: "green" | "blue" | "purple";
    variant?: "bars" | "wave" | "dots";
    barCount?: number;
    // When provided, bar heights are driven by real audio levels instead of CSS animation.
    getVolume?: () => number;
}

// Per-bar multipliers so bars have different heights — gives a natural equalizer look.
const BAR_MULTIPLIERS = [0.5, 1.0, 0.7, 0.4];

export default function EnhancedAudioVisualizer({
    isActive,
    className,
    color = "green",
    variant = "bars",
    barCount = 5,
    getVolume,
}: EnhancedAudioVisualizerProps) {
    // Refs for direct DOM mutation at 60fps — avoids setState overhead.
    const barRefs = useRef<(HTMLDivElement | null)[]>([]);
    const getVolumeRef = useRef(getVolume);
    useEffect(() => { getVolumeRef.current = getVolume; }, [getVolume]);

    useEffect(() => {
        if (!getVolume || !isActive || variant !== "bars") return;
        let raf: number;
        let smoothed = 0;
        const tick = () => {
            const v = Math.min(1, (getVolumeRef.current?.() ?? 0) * 2.5);
            smoothed = smoothed * 0.65 + v * 0.35;
            barRefs.current.forEach((el, i) => {
                if (!el) return;
                // Drive the level via a vertical SCALE (transform), not the layout
                // height — transforms don't affect layout, so the bars' box stays a
                // fixed height and the surrounding "You" label no longer bobs up/down.
                const scale = Math.min(
                    1,
                    Math.max(0.1, smoothed * BAR_MULTIPLIERS[i % BAR_MULTIPLIERS.length] + 0.08),
                );
                el.style.transform = `scaleY(${scale})`;
            });
            raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, [isActive, getVolume, variant]);
    const colorClasses = {
        green: "bg-green-500",
        blue: "bg-blue-500",
        purple: "bg-blue-500",
    };

    const baseColor = colorClasses[color];

    if (variant === "wave") {
        // Map of animation classes for each bar position
        const waveAnimations = [
            'animate-wave-1',
            'animate-wave-2',
            'animate-wave-3',
            'animate-wave-2',
            'animate-wave-1',
        ];

        // Determine how many bars to show based on barCount prop
        // For a true diamond shape, use an odd number of bars
        const barsToShow = Math.min(barCount, waveAnimations.length);

        // Create a diamond pattern - more pronounced
        const diamondHeights = [0.6, 0.9, 1.0, 0.9, 0.6];

        return (
            <div
                className={cn(
                    "flex items-center justify-center gap-[2px] h-6 transition-opacity duration-300",
                    !isActive && "opacity-0",
                    className
                )}
            >
                {[...Array(barsToShow)].map((_, i) => {
                    // Use pre-defined heights for the diamond pattern
                    const heightScale = diamondHeights[i % diamondHeights.length];

                    return (
                        <div
                            key={i}
                            className={cn(
                                "w-[4px] rounded-full will-change-[height]",
                                baseColor,
                                isActive ? waveAnimations[i % waveAnimations.length] : "h-1",
                                // Add subtle variation in opacity based on position
                                i === Math.floor(barsToShow / 2) ? 'opacity-100' : 'opacity-90',
                            )}
                            style={{
                                animationDelay: `${(i * 100) % 500}ms`, // Shorter delay cycle
                                '--bar-scale': heightScale,
                            } as React.CSSProperties}
                        />
                    );
                })}
            </div>
        );
    }

    if (variant === "dots") {
        return (
            <div
                className={cn(
                    "flex items-center justify-center gap-1 transition-opacity duration-300",
                    !isActive && "opacity-0",
                    className
                )}>
                {[...Array(5)].map((_, i) => (
                    <div
                        key={i}
                        className={cn(
                            "rounded-full transition-all",
                            baseColor,
                            isActive && `animate-pulse-scale-${i + 1}`
                        )}
                        style={{
                            width: isActive ? "6px" : "4px",
                            height: isActive ? "6px" : "4px",
                            animationDelay: `${i * 0.15}s`,
                        }}
                    />
                ))}
            </div>
        );
    }

    // Default bars variant
    const useRealAudio = !!getVolume;
    return (
        <div
            className={cn(
                "flex items-center justify-center gap-1 transition-opacity duration-300",
                !isActive && "opacity-0",
                className
            )}
        >
            {[...Array(4)].map((_, i) => (
                <div
                    key={i}
                    ref={(el) => { barRefs.current[i] = el; }}
                    className={cn(
                        "w-1 rounded-full",
                        baseColor,
                        !useRealAudio && (isActive ? "animate-equalizer will-change-height" : "h-0.5"),
                    )}
                    style={
                        !useRealAudio
                            ? { animationDelay: `${i * 0.15}s` }
                            : {
                                // Fixed layout height; the rAF loop scales it via
                                // transform so the container/box never changes size.
                                height: "20px",
                                transform: "scaleY(0.1)",
                                transformOrigin: "center",
                                willChange: "transform",
                            }
                    }
                />
            ))}
        </div>
    );
}
